// CB:BEGIN admin-contractor-dashboard-view
/**
 * ContractorDashboardView — Admin view of a contractor's dashboard.
 *
 * Shows:
 *  - Compact platform status (verification, Stripe, service area)
 *  - For each accepted/active project: NextActionBanner + P0ProjectFlow
 *    rendered with role='contractor' — exactly what the contractor sees
 *  - Pending bids summary list
 */
import React, { useState, useEffect } from 'react';
import { useApi } from '@/lib/useApi';
import { NextActionBanner } from '../NextActionBanner';
import { P0ProjectFlow } from '../P0ProjectFlow';
import { getStatusPill } from '../../styles/adminTokens';

interface ContractorDashboardViewProps {
  contractorId: string;
  onNavigate: (screen: string) => void;
  onEdit?: () => void;
}

const ContractorDashboardView: React.FC<ContractorDashboardViewProps> = ({ contractorId, onNavigate, onEdit }) => {
  const api = useApi();
  const [contractor, setContractor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/admin/contractors/${contractorId}`)
      .then((data: any) => setContractor(data))
      .catch((err: any) => setError(err?.message ?? 'Failed to load contractor'))
      .finally(() => setLoading(false));
  }, [contractorId]);

  if (loading) {
    return (
      <div style={{ padding: '32px 24px', textAlign: 'center', color: '#6b7280' }}>
        Loading contractor view…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', color: '#dc2626', fontSize: 14 }}>
        {error}
      </div>
    );
  }

  if (!contractor) {
    return (
      <div style={{ padding: '40px 24px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
        Contractor not found.
      </div>
    );
  }

  const bids: any[] = contractor.bids ?? [];
  const acceptedBids = bids.filter((b: any) => b.isAccepted && b.project);
  const pendingBids = bids.filter((b: any) => !b.isAccepted && b.project);

  const ver = contractor.contractor_verification;
  const verStatus: string = ver?.verificationStatus ?? 'NOT_STARTED';
  const verColor = verStatus === 'APPROVED' ? '#10b981' : verStatus === 'REJECTED' ? '#dc2626' : '#f59e0b';

  return (
    <div style={{ padding: '24px', maxWidth: 900 }}>

      {/* ── Platform status bar ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 20, marginBottom: 28,
        padding: '16px 20px', background: '#f9fafb', borderRadius: 10,
        border: '1px solid #e5e7eb', fontSize: 13,
      }}>
        <div>
          <span style={{ color: '#6b7280' }}>Company </span>
          <strong style={{ color: '#111827' }}>{contractor.company || '—'}</strong>
        </div>
        <div>
          <span style={{ color: '#6b7280' }}>Verification </span>
          <strong style={{ color: verColor }}>{verStatus.replace(/_/g, ' ')}</strong>
        </div>
        <div>
          <span style={{ color: '#6b7280' }}>Stripe charges </span>
          <strong style={{ color: contractor.stripeChargesEnabled ? '#10b981' : '#9ca3af' }}>
            {contractor.stripeChargesEnabled ? 'enabled' : 'not enabled'}
          </strong>
        </div>
        <div>
          <span style={{ color: '#6b7280' }}>Payouts </span>
          <strong style={{ color: contractor.stripePayoutsEnabled ? '#10b981' : '#9ca3af' }}>
            {contractor.stripePayoutsEnabled ? 'enabled' : 'not enabled'}
          </strong>
        </div>
        {/* CB:BEGIN admin-service-area-display */}
        {contractor.serviceArea?.baseLat && (
          <div>
            <span style={{ color: '#6b7280' }}>Service area </span>
            <strong style={{ color: '#374151' }}>
              {[contractor.serviceArea.baseAddressLine1, contractor.serviceArea.baseCity, contractor.serviceArea.baseState].filter(Boolean).join(', ')}
              {' — '}{contractor.serviceArea.bidRadiusMiles} mi
            </strong>
          </div>
        )}
        {/* CB:END admin-service-area-display */}
        {onEdit && (
          <button
            onClick={onEdit}
            style={{
              marginLeft: 'auto', fontSize: 12, padding: '4px 12px',
              border: '1px solid #d1d5db', borderRadius: 6, background: 'white',
              color: '#374151', cursor: 'pointer',
            }}
          >
            Edit profile
          </button>
        )}
      </div>

      {/* ── Active / accepted projects — exactly what contractor sees ── */}
      {acceptedBids.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>
            Active projects ({acceptedBids.length})
          </div>
          {acceptedBids.map((bid: any) => {
            const project = bid.project;
            const pill = getStatusPill(project.status);
            return (
              <div key={bid.id} style={{ marginBottom: 32 }}>
                {/* Project header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{project.address}</div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                      Created {new Date(project.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  </div>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: pill.bg, color: pill.text,
                  }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: pill.dot, display: 'inline-block' }} />
                    {pill.label}
                  </span>
                </div>

                {/* NextActionBanner — exactly what the contractor sees */}
                <div style={{ marginBottom: 14 }}>
                  <NextActionBanner projectId={project.id} />
                </div>

                {/* P0ProjectFlow — same component the contractor sees, role='contractor' */}
                <P0ProjectFlow
                  projectId={project.id}
                  role="contractor"
                  onUpdated={() => {}}
                />
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pending bids (not yet accepted) ── */}
      {pendingBids.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>
            Pending bids ({pendingBids.length})
          </div>
          {pendingBids.map((bid: any) => {
            const project = bid.project;
            const pill = getStatusPill(project.status);
            return (
              <div key={bid.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10,
                padding: '12px 16px', background: '#fff', border: '1px solid #e5e7eb',
                borderRadius: 8, fontSize: 13,
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>{project.address}</div>
                  <div style={{ color: '#6b7280', marginTop: 2, fontSize: 12 }}>
                    Bid submitted {new Date(bid.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {bid.amount ? ` · $${Number(bid.amount).toLocaleString()}` : ''}
                  </div>
                </div>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                  background: pill.bg, color: pill.text,
                }}>
                  <span style={{ width: 5, height: 5, borderRadius: '50%', background: pill.dot, display: 'inline-block' }} />
                  {pill.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {acceptedBids.length === 0 && pendingBids.length === 0 && (
        <div style={{ padding: '40px 24px', textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
          This contractor has no bids yet.
        </div>
      )}
    </div>
  );
};

export default ContractorDashboardView;
// CB:END admin-contractor-dashboard-view
